import React from "react";
import PropTypes from "prop-types";
import {
  FormattedMessage,
  NotebookImage,
  VideoPlayer,
} from "@learningblocks/shared-components";
import { Collapsible } from "@labxchange/ui-components/lib/LxCollapsible";
import { FullWidthAnimation } from "@learningblocks/shared-components/lib/FullWidthAnimation";
import { sanitizeConfigOptions } from "@labxchange/util-sanitization";
import className from "./style.module.scss";

import { LinkIcon } from "@learningblocks/shared-components/lib/Icons";

const normalizeCollapsibleContent = (content) => {
  if (typeof content === "string") {
    return [{ type: "BODY_TEXT", children: content }];
  }
  return content;
};

const renderCollapsibleItem = (preview) => (item, index) => {
  switch (item.type) {
    case "BODY_TEXT":
      return (
        <FormattedMessage
          key={index}
          string={item.children}
          sanitizeConfig={sanitizeConfigOptions.BlockText}
          description=""
        />
      );
    case "FIGURE":
      return (
        <NotebookImage
          key={index}
          image={{
            src: item.src,
            url: item.url,
            altText: item.altText,
            caption: item.caption,
          }}
          className={className.collapsibleFigure}
          preview={preview}
        />
      );
    default:
      return null;
  }
};

export function LearnMore(props) {
  return (
    <div className={className.learnMoreContainer}>
      {props.image.src !== "" ? (
        <NotebookImage
          image={props.image}
          className={props.className}
          preview={props.preview}
        />
      ) : null}
      {props.animation.src !== "" ? (
        <FullWidthAnimation
          src={props.animation.src}
          url={props.animation.url}
          altText={props.animation.altText}
          caption={props.animation.caption}
          loop={props.animation.loop}
          autoplay={props.animation.autoplay}
          preview={props.preview}
        />
      ) : null}
      {props.video.videoURL !== "" ? (
        <VideoPlayer data={props.video} preview={props.preview} />
      ) : null}
      {props.text !== null ? (
        <div className={className.textBlock}>
          <FormattedMessage
            string={props.text}
            sanitizeConfig={sanitizeConfigOptions.BlockText}
            description=""
          />
        </div>
      ) : null}
      {props.link.url !== "" ? (
        <button
          className={className.linkBtn}
          tabIndex={0}
          onClick={() => {
            window.open(props.link.url, "_blank");
          }}
        >
          <img
            src={LinkIcon}
            alt={FormattedMessage({
              string: "Open link in a new tab",
            })}
          />
          <p>
            <FormattedMessage
              string={props.link.text}
              sanitizeConfig={sanitizeConfigOptions.ButtonText}
              description=""
            />
          </p>
        </button>
      ) : null}
      {props.collapsibles && props.collapsibles.length > 0
        ? props.collapsibles.map((collapsible, index) => (
            <Collapsible
              key={index}
              label={collapsible.label}
              open={collapsible.open}
              variant={collapsible.variant}
              content={normalizeCollapsibleContent(collapsible.content).map(
                renderCollapsibleItem(props.preview)
              )}
            />
          ))
        : null}
    </div>
  );
}

LearnMore.propTypes = {
  image: PropTypes.object,
  video: PropTypes.object,
  text: PropTypes.string,
  link: PropTypes.object,
  preview: PropTypes.bool,
  collapsibles: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      open: PropTypes.bool,
      variant: PropTypes.oneOf(["sleek", "box"]),
      content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string.isRequired,
            children: PropTypes.string,
            src: PropTypes.string,
            url: PropTypes.string,
            altText: PropTypes.string,
            caption: PropTypes.string,
            equation: PropTypes.bool,
          })
        ),
      ]),
    })
  ),
};
LearnMore.defaultProps = {
  image: null,
  video: null,
  text: null,
  link: null,
  preview: false,
  collapsibles: [],
};
